/**
 * Rechte-Katalog (Story 28.2, Change „Rollen und Rechte“ §4.2–§4.4, AD-43): die einzige Quelle aller Rechte im Code.
 * Rollen sind Daten (28.3); welche Rechte es gibt, steht nur hier. Browser-sicher: keine Datenbank, keine Uhr, kein
 * Netz, kein Node-Modul.
 *
 * Arten:
 * - **Bereiche** mit drei Stufen `none | read | write` (Schlüssel `<bereich>:read` und `<bereich>:write`; Bearbeiten
 *   schließt Sehen ein).
 * - **Sonderrechte** (`<gruppe>:<name>`), manche mit Teil-Umfängen („nur Aufgaben“, „nur Lieferschein“ …) und
 *   Abhängigkeiten (`payments:book` wirkt nur mit `payments:read`).
 * - **Nur Admin** (`adminOnly`): nie vergebbar, nur die fest eingebaute Admin-Rolle hat sie.
 * - **Immer:** `time:own` (eigene Zeiterfassung) hat jede Rolle.
 *
 * Neue Rechte kommen nur über diesen Katalog (mit Startwert je Grundrolle in `builtin-roles.ts`) und erhöhen
 * `PERMISSION_CATALOG_VERSION`.
 */

import { deepFreeze } from './deep-freeze.ts';

/** Version des Katalogs; steht an jeder Rollen-Fassung (28.3) und an jedem aufgelösten Ergebnis. */
export const PERMISSION_CATALOG_VERSION = 3;

/** Stufen eines Bereichs (Change §4.3). */
export const permissionLevels = ['none', 'read', 'write'] as const;
export type PermissionLevel = (typeof permissionLevels)[number];

/** Deutsche Namen der Stufen (Entwurf 28.1). */
export const permissionLevelLabels: Readonly<Record<PermissionLevel, string>> = deepFreeze({
  none: 'Kein Zugriff',
  read: 'Sehen',
  write: 'Bearbeiten',
});

/** Bereiche in der Reihenfolge des Entwurfs 28.1. */
export const permissionAreaKeys = [
  'start',
  'orders',
  'customers',
  'returns',
  'articles',
  'purchasing',
  'shipping',
  'labels',
  'documents',
  'payments',
  'receivables',
  'datev',
  'reports',
  'tasks',
  'chat',
  'ai',
] as const;
export type PermissionArea = (typeof permissionAreaKeys)[number];

/** Gruppen der Bereiche (Überschriften in Einstellungen › Rollen und Rechte). */
export const permissionAreaGroups = ['Verkauf', 'Lager und Einkauf', 'Finanzen', 'Zusammenarbeit'] as const;
export type PermissionAreaGroup = (typeof permissionAreaGroups)[number];

export interface PermissionAreaEntry {
  key: PermissionArea;
  group: PermissionAreaGroup;
  /** Deutscher Name. */
  name: string;
  /** Erklärsatz je Stufe: was die Stufe in diesem Bereich bedeutet. */
  levelTexts: Readonly<Record<PermissionLevel, string>>;
}

export const PERMISSION_AREAS: readonly PermissionAreaEntry[] = deepFreeze<PermissionAreaEntry[]>([
  {
    key: 'start',
    group: 'Verkauf',
    name: 'Start',
    levelTexts: {
      none: 'Startseite fehlt; man landet im ersten erlaubten Bereich.',
      read: 'Kacheln ansehen.',
      write: 'Kacheln ansehen und anordnen. Zahlen nur aus erlaubten Bereichen.',
    },
  },
  {
    key: 'orders',
    group: 'Verkauf',
    name: 'Aufträge und Angebote',
    levelTexts: {
      none: 'Fehlt im Menü, in Suche und Glocke.',
      read: 'Aufträge öffnen und suchen, nichts ändern.',
      write: 'Aufträge und Angebote anlegen und ändern.',
    },
  },
  {
    key: 'customers',
    group: 'Verkauf',
    name: 'Kunden',
    levelTexts: {
      none: 'Fehlt im Menü; Verweise zeigen nur die Kundennummer.',
      read: 'Kunden öffnen und suchen, nichts ändern.',
      write: 'Kunden anlegen und ändern.',
    },
  },
  {
    key: 'returns',
    group: 'Verkauf',
    name: 'Retouren und Gutschriften',
    levelTexts: {
      none: 'Fehlt im Menü und in der Suche.',
      read: 'Retouren ansehen, nichts ändern.',
      write: 'Retouren anlegen, prüfen und abschließen.',
    },
  },
  {
    key: 'articles',
    group: 'Lager und Einkauf',
    name: 'Artikel und Lager',
    levelTexts: {
      none: 'Fehlt im Menü und in der Suche.',
      read: 'Artikel und Bestände ansehen, nichts ändern.',
      write: 'Artikel anlegen, Bestände buchen.',
    },
  },
  {
    key: 'purchasing',
    group: 'Lager und Einkauf',
    name: 'Einkauf',
    levelTexts: {
      none: 'Lieferanten, Bestellungen und Wareneingang fehlen.',
      read: 'Bestellungen ansehen, nichts ändern.',
      write: 'Bestellen und Wareneingang buchen.',
    },
  },
  {
    key: 'shipping',
    group: 'Lager und Einkauf',
    name: 'Versand',
    levelTexts: {
      none: 'Fehlt im Menü.',
      read: 'Sendungen ansehen, nichts ändern.',
      write: 'Packen, Labels drucken, Sendungen ändern.',
    },
  },
  {
    key: 'labels',
    group: 'Lager und Einkauf',
    name: 'Etiketten-Vorlagen',
    levelTexts: {
      none: 'Etiketten-Galerie fehlt.',
      read: 'Vorlagen ansehen und drucken.',
      write: 'Vorlagen gestalten und ändern.',
    },
  },
  {
    key: 'documents',
    group: 'Finanzen',
    name: 'Belege',
    levelTexts: {
      none: 'Rechnungen und Gutschriften fehlen.',
      read: 'Belege öffnen und herunterladen.',
      write: 'Belege vorbereiten und ändern. Ausstellen ist ein Sonderrecht.',
    },
  },
  {
    key: 'payments',
    group: 'Finanzen',
    name: 'Zahlungseingänge',
    levelTexts: {
      none: 'Bank, PayPal und Kontoauszüge fehlen. Am Auftrag steht nur „bezahlt · teilweise · offen“.',
      read: 'Bank- und PayPal-Umsätze und Kontoauszüge sehen.',
      write: 'Zahlungsabgleich bearbeiten. Buchen ist ein Sonderrecht.',
    },
  },
  {
    key: 'receivables',
    group: 'Finanzen',
    name: 'Offene Posten und Mahnungen',
    levelTexts: {
      none: 'Fehlt im Menü.',
      read: 'Offene Posten ansehen, nicht mahnen.',
      write: 'Mahnungen vorbereiten und senden. Zahlungszeilen nur mit „Zahlungseingänge“.',
    },
  },
  {
    key: 'datev',
    group: 'Finanzen',
    name: 'DATEV-Export',
    levelTexts: {
      none: 'Fehlt im Menü.',
      read: 'Exporte ansehen und herunterladen.',
      write: 'Exporte erstellen, prüfen, zurückhalten und an die Steuerberatung senden.',
    },
  },
  {
    key: 'reports',
    group: 'Finanzen',
    name: 'Berichte',
    levelTexts: {
      none: 'Fehlt im Menü.',
      read: 'Berichte ansehen.',
      write: 'Berichte ansehen und eigene Ansichten speichern.',
    },
  },
  {
    key: 'tasks',
    group: 'Zusammenarbeit',
    name: 'Aufgaben',
    levelTexts: {
      none: 'Fehlt im Menü.',
      read: 'Aufgaben ansehen.',
      write: 'Aufgaben anlegen, zuweisen und erledigen.',
    },
  },
  {
    key: 'chat',
    group: 'Zusammenarbeit',
    name: 'Team-Chat',
    levelTexts: {
      none: 'Fehlt im Menü.',
      read: 'Mitlesen.',
      write: 'Schreiben und Dateien teilen.',
    },
  },
  {
    key: 'ai',
    group: 'Zusammenarbeit',
    name: 'KI-Assistent „Frag TILL“',
    levelTexts: {
      none: 'Frage-Feld fehlt.',
      read: 'Fragen stellen; Antworten nur aus erlaubten Bereichen.',
      write: 'Fragen stellen und Entwürfe anlegen lassen.',
    },
  },
]);

/** Schlüssel eines Bereichs-Rechts: `<bereich>:read` oder `<bereich>:write`. */
export type AreaPermissionKey = `${PermissionArea}:read` | `${PermissionArea}:write`;

/** Sonderrechte in der Reihenfolge des Entwurfs 28.1. */
export const specialPermissionKeys = [
  'prices:sales',
  'prices:purchase',
  'data:export',
  'data:delete',
  'outbound:send',
  'data:bulk',
  'documents:issue',
  'payments:book',
  'privacy:manage',
  'rules:publish',
  'settings:edit',
  'settings:finance',
  'ai:tasks',
  'mcp:ownKey',
  'time:leave',
  'chat:team',
] as const;
export type SpecialPermissionKey = (typeof specialPermissionKeys)[number];

/** Nur-Admin-Rechte (Change §4.4 Nr. 1): nie vergebbar. */
export const adminOnlyPermissionKeys = [
  'admin:counterparts',
  'admin:switches',
  'admin:users',
  'admin:cutover',
  'admin:mcpKeys',
  'admin:restore',
] as const;
export type AdminOnlyPermissionKey = (typeof adminOnlyPermissionKeys)[number];

/**
 * Rechte der Admin-Klasse (Story 28.4, AD-20): ersetzen das frühere `role: 'admin'` der Commands. Sie gehen nie an
 * Actor `mcp`, `ai` oder `rule` (`actorTypesNeverAdmin`), auch wenn eine Rolle sie hat; eine KI führt solche Commands nie
 * aus. Dazu gehören alle Nur-Admin-Rechte und die vergebbaren Einstellungs-, Regel- und Datenschutz-Rechte.
 */
export const adminClassPermissionKeys = ['privacy:manage', 'rules:publish', 'settings:edit', 'settings:finance', ...adminOnlyPermissionKeys] as const;
export type AdminClassPermissionKey = (typeof adminClassPermissionKeys)[number];

/** Gehört das Recht zur Admin-Klasse (nie an Maschinen-Actors)? */
export function isAdminClassPermission(key: string): key is AdminClassPermissionKey {
  return (adminClassPermissionKeys as readonly string[]).includes(key);
}

/** Rechte, die jede Rolle hat (eigene Zeiterfassung; die Grenze je Person bleibt in `staff`, AD-38). */
export const alwaysGrantedPermissionKeys = ['time:own'] as const;
export type AlwaysGrantedPermissionKey = (typeof alwaysGrantedPermissionKeys)[number];

export type PermissionKey = AreaPermissionKey | SpecialPermissionKey | AdminOnlyPermissionKey | AlwaysGrantedPermissionKey;

export interface SpecialPermissionEntry {
  key: SpecialPermissionKey;
  /** Deutscher Name. */
  name: string;
  /** Erklärsatz. */
  description: string;
  /**
   * Teil-Umfänge (Schlüssel → deutscher Text). Ein Teil-Umfang gewährt nie das volle Recht; das volle Recht schließt
   * jeden Teil ein.
   */
  scopes: Readonly<Record<string, string>>;
  /** Rechte, ohne die dieses Sonderrecht nicht wirkt (z. B. `payments:book` braucht `payments:read`). */
  requires: readonly PermissionKey[];
  /** Freischalten nur mit Bestätigung „Abnahme durch Steuerberater/Rudolf nötig“ (Change §4.4 Nr. 2). */
  financeNotice: boolean;
}

export const SPECIAL_PERMISSIONS: readonly SpecialPermissionEntry[] = deepFreeze<SpecialPermissionEntry[]>([
  {
    key: 'prices:sales',
    name: 'Verkaufspreise, Beträge und Umsätze sehen',
    description: 'Ohne dieses Recht steht in Listen, Belegen, Export und KI „—“.',
    scopes: {},
    requires: [],
    financeNotice: false,
  },
  {
    key: 'prices:purchase',
    name: 'Einkaufspreise, Margen und Lagerwert sehen',
    description: 'Ohne dieses Recht steht bei EK, Einstandspreis und Marge „—“.',
    scopes: {},
    requires: [],
    financeNotice: false,
  },
  {
    key: 'data:export',
    name: 'Export (Liste als Datei)',
    description: 'Listen als Excel oder CSV herunterladen.',
    scopes: { tasks: 'nur Aufgaben' },
    requires: [],
    financeNotice: false,
  },
  {
    key: 'data:delete',
    name: 'Löschen und Stornieren',
    description: 'Belege stornieren, Aufträge löschen, Papierkorb leeren.',
    scopes: { ownTasks: 'eigene Aufgaben' },
    requires: [],
    financeNotice: false,
  },
  {
    key: 'outbound:send',
    name: 'Nach außen senden',
    description: 'E-Mail an Kunden, Shop, eBay, DHL beauftragen.',
    scopes: { dhlLabel: 'nur DHL-Label' },
    requires: [],
    financeNotice: false,
  },
  {
    key: 'data:bulk',
    name: 'Massenbearbeitung',
    description: 'Mehrere Zeilen auf einmal ändern.',
    scopes: { tasks: 'nur Aufgaben' },
    requires: [],
    financeNotice: false,
  },
  {
    key: 'documents:issue',
    name: 'Belege ausstellen',
    description: 'Rechnung, Gutschrift und Lieferschein endgültig ausstellen.',
    scopes: { deliveryNote: 'nur Lieferschein' },
    requires: [],
    financeNotice: false,
  },
  {
    key: 'payments:book',
    name: 'Zahlungen buchen und zuordnen',
    description: 'Zahlungsabgleich, „Automatisch buchen“, Zuordnen von Hand.',
    scopes: {},
    requires: ['payments:read'],
    financeNotice: false,
  },
  {
    key: 'privacy:manage',
    name: 'Personendaten: Auskunft und Löschung',
    description: 'Auskunft erstellen und Personen anonymisieren.',
    scopes: {},
    requires: [],
    financeNotice: false,
  },
  {
    key: 'rules:publish',
    name: 'Regeln veröffentlichen, Prüfungen ändern',
    description: 'Automatik-Regeln scharf schalten.',
    scopes: {},
    requires: [],
    financeNotice: false,
  },
  {
    key: 'settings:edit',
    name: 'Fachliche Einstellungen ändern',
    description: 'Versandarten, Textbausteine, Vorlagen.',
    scopes: {},
    requires: [],
    financeNotice: false,
  },
  {
    key: 'settings:finance',
    name: 'Steuer- und Finanz-Einstellungen ändern',
    description: 'Steuerregeln, Konten, Mahnstufen, Nummernkreise.',
    scopes: {},
    requires: [],
    financeNotice: true,
  },
  {
    key: 'ai:tasks',
    name: 'KI-Arbeiten auf Anweisung',
    description: 'KI legt auf Bitte Entwürfe an – nie Buchung oder Versand.',
    scopes: {},
    requires: [],
    financeNotice: false,
  },
  {
    key: 'mcp:ownKey',
    name: 'Eigener MCP-Schlüssel',
    description: 'Eigene KI-Werkzeuge mit den eigenen Rechten verbinden.',
    scopes: {},
    requires: [],
    financeNotice: false,
  },
  {
    // Story 28.4 (Katalog-Version 2): Der Packplatz stempelt nur [RUDOLF 29.10.2026]; Urlaub, Nachträge, Kalender und
    // Team brauchen dieses Recht. Die Grenze je Person bleibt in `staff` (AD-38).
    key: 'time:leave',
    name: 'Zeiterfassung: Urlaub, Nachträge und Kalender',
    description: 'Urlaub beantragen, Zeiten nachtragen, Kalender und Team ansehen. Stempeln ist immer erlaubt.',
    scopes: {},
    requires: [],
    financeNotice: false,
  },
  {
    // Story 24.1 (Katalog-Version 3, [HS55 03.11.2026]): Team-Chat für alle Rollen (Vorschlag AD-40). Der Bereich `chat`
    // bleibt der Chatter am Datensatz (28.4); so kommt der Packplatz über den Team-Chat nicht an Kundendaten.
    key: 'chat:team',
    name: 'Team-Chat',
    description: 'Kanäle sehen, beitreten und schreiben. Nachrichten sieht nur, wer im Kanal Mitglied ist – auch kein Admin.',
    scopes: {},
    requires: [],
    financeNotice: false,
  },
]);

export interface AdminOnlyPermissionEntry {
  key: AdminOnlyPermissionKey;
  name: string;
}

export const ADMIN_ONLY_PERMISSIONS: readonly AdminOnlyPermissionEntry[] = deepFreeze<AdminOnlyPermissionEntry[]>([
  { key: 'admin:counterparts', name: 'Zugänge zu Gegenstellen' },
  { key: 'admin:switches', name: 'Betriebsschalter aus · Probebetrieb · live' },
  { key: 'admin:users', name: 'Benutzer und Rollen verwalten' },
  { key: 'admin:cutover', name: 'Umschalttag' },
  { key: 'admin:mcpKeys', name: 'MCP-Schlüssel anderer Personen sperren' },
  { key: 'admin:restore', name: 'Wiederherstellung und Systemstatus' },
]);

export interface AlwaysGrantedPermissionEntry {
  key: AlwaysGrantedPermissionKey;
  name: string;
}

export const ALWAYS_GRANTED_PERMISSIONS: readonly AlwaysGrantedPermissionEntry[] = deepFreeze([
  { key: 'time:own', name: 'Zeiterfassung, eigene' },
]);

/** Feste deutsche Sätze rund um Rechte. */
export const PERMISSION_TEXTS = deepFreeze({
  /** Server 403, Commands, MCP (Change §4.3). */
  forbidden: 'Dafür haben Sie keine Berechtigung.',
  /** Grau mit Schloss in der Matrix (Change §4.4 Nr. 1). */
  adminOnly: 'Nur Admin – zum Schutz Ihrer Zugänge und Schalter',
  /** Bestätigung beim Freischalten der Steuer- und Finanz-Einstellungen (Change §4.4 Nr. 2). */
  financeNotice: 'Abnahme durch Steuerberater/Rudolf nötig',
  /** Zeiterfassung ist immer dabei. */
  always: 'Immer erlaubt',
} as const);

/** Der Katalog als ein Wert (Eingabe des reinen Kerns `resolvePermissions`). */
export interface PermissionCatalog {
  version: number;
  areas: readonly PermissionAreaEntry[];
  specials: readonly SpecialPermissionEntry[];
  adminOnly: readonly AdminOnlyPermissionEntry[];
  always: readonly AlwaysGrantedPermissionEntry[];
}

export const PERMISSION_CATALOG: PermissionCatalog = deepFreeze({
  version: PERMISSION_CATALOG_VERSION,
  areas: PERMISSION_AREAS,
  specials: SPECIAL_PERMISSIONS,
  adminOnly: ADMIN_ONLY_PERMISSIONS,
  always: ALWAYS_GRANTED_PERMISSIONS,
});

/** Alle Schlüssel eines Katalogs: Bereiche (Sehen, Bearbeiten), Sonderrechte, Nur-Admin, Immer. */
export function catalogPermissionKeys(catalog: PermissionCatalog): PermissionKey[] {
  return [
    ...catalog.areas.flatMap((a) => [`${a.key}:read`, `${a.key}:write`] as AreaPermissionKey[]),
    ...catalog.specials.map((s) => s.key),
    ...catalog.adminOnly.map((a) => a.key),
    ...catalog.always.map((a) => a.key),
  ];
}

/** Alle Schlüssel des aktuellen Katalogs. */
export const permissionKeys: readonly PermissionKey[] = Object.freeze(catalogPermissionKeys(PERMISSION_CATALOG));

const permissionKeySet: ReadonlySet<string> = new Set(permissionKeys);

/** Ist das ein Schlüssel des aktuellen Katalogs? */
export function isPermissionKey(value: unknown): value is PermissionKey {
  return typeof value === 'string' && permissionKeySet.has(value);
}

/** Ist das ein Nur-Admin-Recht? */
export function isAdminOnlyPermission(key: string): key is AdminOnlyPermissionKey {
  return (adminOnlyPermissionKeys as readonly string[]).includes(key);
}

/** Deutscher Name eines Rechts (Bereich mit Stufe „Sehen“/„Bearbeiten“, Sonderrecht, Nur-Admin, Immer); unbekannt → `null`. */
export function permissionLabel(key: string, catalog: PermissionCatalog = PERMISSION_CATALOG): string | null {
  const parts = key.split(':');
  if (parts.length !== 2) return null;
  const [head, tail] = parts;
  const area = catalog.areas.find((a) => a.key === head);
  if (area && (tail === 'read' || tail === 'write')) return `${area.name}: ${permissionLevelLabels[tail]}`;
  return (
    catalog.specials.find((s) => s.key === key)?.name ??
    catalog.adminOnly.find((a) => a.key === key)?.name ??
    catalog.always.find((a) => a.key === key)?.name ??
    null
  );
}
